/**
 * Equipment value comparator for case + format-insensitive matching between
 * Angular Material `<mat-select>` options and stored equipment values.
 *
 * Necessary because four different vocabularies arrive at the same dropdown:
 *
 *   - dbVal (mat-option [value]):  UPPERCASE-with-SPACES — `FLATBED`,
 *     `STEPDECK`, `POWER ONLY`, `DOUBLE DROP`, `RGN`
 *   - canonical (Mercury / carrier-hub post-Phase-1.c): lowercase-with-
 *     underscores — `flatbed`, `step_deck`, `power_only`, `double_drop`,
 *     `removable_gooseneck`
 *   - Title-case stragglers (carrier-hub rows from the rel20260505 → 2026-05-19
 *     window, before the §13.1 hotfix landed): `Flatbed`, `Step_Deck`,
 *     `Power_Only`, `Double_Drop`, `Rgn`
 *   - Legacy UPPERCASE: `FLATBED`, `STEP_DECK`, `POWER_ONLY`, …
 *
 * The default `===` `compareWith` cannot match any of those representations
 * against each other. This function normalizes both sides (lowercase + strip
 * underscores and spaces) and applies one alias (`removable_gooseneck` ↔
 * `RGN`) which can't be derived structurally.
 *
 * Plan reference: §13.6 — c4-client side of the equipment-vocabulary
 * canonicalization. Goes alongside the Java `EquipmentTranslation` (janus,
 * Phase 6.5) and the Python `MERCURY_TO_CARRIER_HUB_EQUIPMENT_NAME`
 * (mcleod/carriers, Phase 1.b.0).
 */

/** Names that map to the same dbVal but aren't structurally identical. */
const ALIASES: Record<string, string> = {
	rgn: 'rgn',
	removablegooseneck: 'rgn',
};

function canonicalKey(value: string): string {
	const stripped = value.trim().toLowerCase().replace(/[_\s-]/g, '');
	return ALIASES[stripped] ?? stripped;
}

/**
 * Returns `true` if the two equipment strings refer to the same equipment
 * class regardless of case, separator (`_`, ` `, `-`), or known alias.
 *
 * Pass as the `[compareWith]` input on any `<mat-select>` whose options bind
 * `[value]="trailer.dbVal"`.
 */
export function equipmentMatches(a: string | null | undefined, b: string | null | undefined): boolean {
	if (!a || !b) {
		return false;
	}
	return canonicalKey(a) === canonicalKey(b);
}
